import React, { useState } from 'react';
import { Box, Typography, IconButton, useTheme } from '@mui/material';
import { DeleteFileAttachIcon, DownloadIconFile } from '@/public/icon';
import CenteredBox from '@/app/base-ui/CenterBox';
import EllipsisTypoGraphyMultiLine from '../EllipsisTypoGraphyMultiLine';
import axios from 'axios';
import fetchWrapper from '@/app/service/fetchWrapper';
import { useGlobalDialog } from '@/app/contexts/global-dialog';

interface FileDisplayProps {
    fileUrl: string;
    fileSize: string;
    base: string;
    idPost?: string;
    extension: string;
    type?: 'download' | 'edit' | 'create';
    handleDeleteFile?: (fileUrl: string) => void;
}

const FileDisplay: React.FC<FileDisplayProps> = ({ handleDeleteFile, type = 'download', fileUrl = '', fileSize = '', base = '', extension = '', idPost }) => {
    const theme = useTheme();
    const [loadingDownPDF, setLoadingDownPDF] = useState<boolean>(false);
    const { loadingDialog } = useGlobalDialog();
    const handleAction = (fileUrl: string, type: 'download' | 'edit' | 'create') => {
        if (type === 'create') {
            handleDeleteFile?.(fileUrl);
        }
    };

    const downloadPdf = async (fileName: string, id: string | undefined) => {
        try {
            if (loadingDownPDF) return;
            setLoadingDownPDF(true);
            loadingDialog.open();
            const linkURl = await fetchWrapper.post(
                `${process.env.BASE_URL}/posts/${id}/download-pdf`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/pdf',
                    },
                    responseType: 'blob',
                }
            );
            const url = window.URL.createObjectURL(new Blob([linkURl.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${fileName}.${extension}`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setLoadingDownPDF(false);
            loadingDialog.close();
        } catch (error) {
            setLoadingDownPDF(false);
            loadingDialog.close();
            console.error('Error downloading the PDF:', error);
        }
    };
    return (
        <CenteredBox
            sx={{ py: 5, height: 68, px: '14px', justifyContent: 'space-between', width: '100%', maxWidth: '100%', backgroundColor: theme.palette.grayscale.gray02, borderRadius: '16px', gap: '17px' }}
        >
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexWrap: 'nowrap',
                        minWidth: 0,
                        maxWidth: '100%',
                        gap: '6px',
                    }}
                >
                    <EllipsisTypoGraphyMultiLine
                        sx={{ wordBreak: 'break-all' }}
                        text={base}
                        lineNumber={1}
                        variant="h2_12Regular"
                        endIcon={`.${extension}`}
                    />

                    <Typography
                        variant="h2_10Regular"
                        lineHeight="100%"
                        sx={{ flexGrow: 1, color: 'var(--text-gray-05999, #999)' }}
                    >
                        {fileSize}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ mr: -2 }}>
                <IconButton
                    disabled={loadingDownPDF}
                    onClick={type === 'download' ? () => downloadPdf(base, idPost) : () => handleAction(fileUrl, type)}
                >
                    {type === 'download' ? <DownloadIconFile /> : type === 'create' ? <DeleteFileAttachIcon /> : null}
                </IconButton>
            </Box>
        </CenteredBox>
    );
};

export default FileDisplay;
